/**
 * Data Reading Test Utilities
 * This file contains utility functions for testing data reading functionality, including mock data and automated test scenarios
 */

interface MockResponse {
    ok?: boolean;
    ready?: boolean;
    delay?: number;
    error?: string;
    response?: any;
    lastSeen?: number;
    batteryLevel?: number;
    signalStrength?: number;
    device_last_publish?: number;
}

interface TestResult {
    success: boolean;
    error?: string;
    completedAt?: number;
}

interface TestRecord {
    scenario: string;
    startTime: Date;
    duration: number;
    result: TestResult;
    logs: any[];
}

interface TestComponent {
    mockMode: boolean;
    debugLogs: any[];
    dataReadingSuccess: boolean;
    dataReadingError: string | null;
    dataReadingCanceled: boolean;
    dataReadingModal: boolean;
    openDataReadingModal(): Promise<void>;
    closeDataReadingModal(): void;
    clearDebugLogs(): void;
}

export class DataReadingTestSuite {
    testScenarios: any;
    currentScenario: string | null;
    testStats: {
        totalTests: number;
        passedTests: number;
        failedTests: number;
        testHistory: TestRecord[];
    };

    constructor() {
        this.testScenarios = {
            success: {
                name: 'Success Scenario',
                description: 'Device responds quickly, data reading successful',
                mockData: {
                    calAwakeMins: { ok: true, delay: 500 },
                    pingDevice: { ready: true, delay: 1000 },
                    publishTrackerData: { ok: true, delay: 1000 },
                    dataCheck: { newDataAfter: 3 } // New data found after 3 checks
                }
            },
            slowDevice: {
                name: 'Slow Device Response',
                description: 'Device needs multiple pings to be ready',
                mockData: {
                    calAwakeMins: { ok: true, delay: 2000 },
                    pingDevice: { ready: false, readyAfterAttempts: 5, delay: 2000 },
                    publishTrackerData: { ok: true, delay: 1500 },
                    dataCheck: { newDataAfter: 6 }
                }
            },
            deviceOffline: {
                name: 'Device Offline',
                description: 'Device cannot be woken up or pinged',
                mockData: {
                    calAwakeMins: { ok: false, error: 'Device not responding', delay: 3000 },
                    pingDevice: { ready: false, delay: 2000 },
                    publishTrackerData: { ok: false, error: 'Device offline' },
                    dataCheck: { newDataAfter: null } // Never get new data
                }
            },
            dataTimeout: {
                name: 'Data Reading Timeout',
                description: 'Data sending successful but no new data detected',
                mockData: {
                    calAwakeMins: { ok: true, delay: 1000 },
                    pingDevice: { ready: true, delay: 1000 },
                    publishTrackerData: { ok: true, delay: 1000 },
                    dataCheck: { newDataAfter: null } // Never get new data
                }
            },
            partialSuccess: {
                name: 'Partial Success',
                description: 'Device responds but data reading fails',
                mockData: {
                    calAwakeMins: { ok: true, delay: 1000 },
                    pingDevice: { ready: true, delay: 1000 },
                    publishTrackerData: { ok: false, error: 'Sensor malfunction', delay: 1000 },
                    dataCheck: { newDataAfter: null }
                }
            }
        };
        
        this.currentScenario = null;
        this.testStats = {
            totalTests: 0,
            passedTests: 0,
            failedTests: 0,
            testHistory: []
        };
    }

    /**
     * Set test scenario
     */
    setTestScenario(scenarioName: string): boolean {
        if (this.testScenarios[scenarioName]) {
            this.currentScenario = scenarioName;
            console.log(`Test scenario set to: ${this.testScenarios[scenarioName].name}`);
            return true;
        }
        console.error(`Unknown test scenario: ${scenarioName}`);
        return false;
    }

    /**
     * Get mock response (based on current test scenario)
     */
    getMockResponse(apiType: string, attemptCount: number = 1): MockResponse {
        if (!this.currentScenario) {
            return this.getDefaultMockResponse(apiType);
        }

        const scenario = this.testScenarios[this.currentScenario];
        const mockData = scenario.mockData;

        switch (apiType) {
            case 'calAwakeMins':
                return {
                    ok: mockData.calAwakeMins.ok,
                    delay: mockData.calAwakeMins.delay,
                    error: mockData.calAwakeMins.error,
                    response: mockData.calAwakeMins.ok ? { return_value: 0 } : null
                };

            case 'pingDevice':
                let ready = mockData.pingDevice.ready;
                // If readyAfterAttempts is set, only ready after specified attempts
                if (mockData.pingDevice.readyAfterAttempts) {
                    ready = attemptCount >= mockData.pingDevice.readyAfterAttempts;
                }
                
                return {
                    ready,
                    delay: mockData.pingDevice.delay,
                    lastSeen: Date.now() / 1000,
                    batteryLevel: Math.floor(Math.random() * 100),
                    signalStrength: Math.floor(Math.random() * 100)
                };

            case 'publishTrackerData':
                return {
                    ok: mockData.publishTrackerData.ok,
                    delay: mockData.publishTrackerData.delay,
                    error: mockData.publishTrackerData.error,
                    response: mockData.publishTrackerData.ok ? {
                        return_value: 1,
                        message: 'Data collection started',
                        dataId: 'mock_' + Date.now()
                    } : null
                };

            case 'deviceHistory':
                const shouldHaveNewData = mockData.dataCheck.newDataAfter && 
                                        attemptCount >= mockData.dataCheck.newDataAfter;
                
                return {
                    device_last_publish: shouldHaveNewData ? 
                        Date.now() / 1000 + Math.random() * 30 : 
                        Date.now() / 1000 - 3600 // Data from 1 hour ago
                };

            default:
                return this.getDefaultMockResponse(apiType);
        }
    }

    /**
     * Default mock response
     */
    getDefaultMockResponse(apiType: string): MockResponse {
        const responses: { [key: string]: MockResponse } = {
            calAwakeMins: {
                ok: true,
                delay: 1000,
                response: { return_value: 0 }
            },
            pingDevice: {
                ready: Math.random() > 0.3,
                delay: 1000,
                lastSeen: Date.now() / 1000,
                batteryLevel: Math.floor(Math.random() * 100),
                signalStrength: Math.floor(Math.random() * 100)
            },
            publishTrackerData: {
                ok: true,
                delay: 1000,
                response: {
                    return_value: 1,
                    message: 'Data collection started',
                    dataId: 'mock_' + Date.now()
                }
            },
            deviceHistory: {
                device_last_publish: Date.now() / 1000 + Math.random() * 30
            }
        };

        return responses[apiType] || { ok: false, error: 'Unknown API type' };
    }

    /**
     * Run automated test
     */
    async runAutomatedTest(scenarioName: string, testComponent: TestComponent): Promise<TestRecord> {
        console.log(`Starting test scenario: ${scenarioName}`);
        const startTime = Date.now();
        
        try {
            this.setTestScenario(scenarioName);
            this.testStats.totalTests++;
            
            // Enable Mock mode
            testComponent.mockMode = true;
            testComponent.clearDebugLogs();
            
            // Open data reading modal
            await testComponent.openDataReadingModal();
            
            // Wait for test completion or timeout
            const testResult = await this.waitForTestCompletion(testComponent, 120000); // 2 minutes timeout
            
            const endTime = Date.now();
            const duration = endTime - startTime;
            
            const testRecord: TestRecord = {
                scenario: scenarioName,
                startTime: new Date(startTime),
                duration,
                result: testResult,
                logs: [...testComponent.debugLogs]
            };
            
            this.testStats.testHistory.push(testRecord);
            
            if (testResult.success) {
                this.testStats.passedTests++;
                console.log(`✅ Test passed: ${scenarioName} (Duration: ${duration}ms)`);
            } else {
                this.testStats.failedTests++;
                console.log(`❌ Test failed: ${scenarioName} - ${testResult.error} (Duration: ${duration}ms)`);
            }
            
            return testRecord;
            
        } catch (error: any) {
            this.testStats.failedTests++;
            console.error(`Test error: ${scenarioName}`, error);
            return {
                scenario: scenarioName,
                startTime: new Date(startTime),
                duration: Date.now() - startTime,
                result: { success: false, error: error.message },
                logs: testComponent.debugLogs || []
            };
        } finally {
            // Clean up test state
            testComponent.closeDataReadingModal();
            testComponent.mockMode = false;
            this.currentScenario = null;
        }
    }

    /**
     * Wait for test completion
     */
    async waitForTestCompletion(testComponent: TestComponent, timeout: number = 120000): Promise<TestResult> {
        return new Promise((resolve) => {
            const startTime = Date.now();
            
            const checkInterval = setInterval(() => {
                const elapsed = Date.now() - startTime;
                
                // Check timeout
                if (elapsed > timeout) {
                    clearInterval(checkInterval);
                    resolve({ success: false, error: 'Test timeout' });
                    return;
                }
                
                // Check success
                if (testComponent.dataReadingSuccess) {
                    clearInterval(checkInterval);
                    resolve({ success: true, completedAt: elapsed });
                    return;
                }
                
                // Check failure
                if (testComponent.dataReadingError) {
                    clearInterval(checkInterval);
                    resolve({ success: false, error: testComponent.dataReadingError });
                    return;
                }
                
                // Check cancelled
                if (testComponent.dataReadingCanceled || !testComponent.dataReadingModal) {
                    clearInterval(checkInterval);
                    resolve({ success: false, error: 'Test cancelled' });
                    return;
                }
            }, 1000);
        });
    }

    /**
     * Run all test scenarios
     */
    async runAllTests(testComponent: TestComponent): Promise<TestRecord[]> {
        console.log('Starting all test scenarios...');
        const results: TestRecord[] = [];
        
        for (const scenarioName of Object.keys(this.testScenarios)) {
            const result = await this.runAutomatedTest(scenarioName, testComponent);
            results.push(result);
            
            // Wait 2 seconds between tests
            await new Promise(resolve => setTimeout(resolve, 2000));
        }
        
        this.printTestSummary();
        return results;
    }

    /**
     * Print test summary
     */
    printTestSummary(): void {
        console.log('\n=== Test Summary ===');
        console.log(`Total tests: ${this.testStats.totalTests}`);
        console.log(`Passed: ${this.testStats.passedTests}`);
        console.log(`Failed: ${this.testStats.failedTests}`);
        console.log(`Success rate: ${((this.testStats.passedTests / this.testStats.totalTests) * 100).toFixed(1)}%`);
        
        console.log('\n=== Test Details ===');
        this.testStats.testHistory.forEach((test, index) => {
            const status = test.result.success ? '✅' : '❌';
            console.log(`${index + 1}. ${status} ${test.scenario} (${test.duration}ms)`);
            if (!test.result.success) {
                console.log(`   Error: ${test.result.error}`);
            }
        });
    }

    /**
     * Export test report
     */
    exportTestReport(): void {
        const report = {
            timestamp: new Date().toISOString(),
            stats: this.testStats,
            scenarios: this.testScenarios
        };
        
        const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `data-reading-test-report-${Date.now()}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        console.log('Test report exported');
    }

    /**
     * Reset test statistics
     */
    resetStats(): void {
        this.testStats = {
            totalTests: 0,
            passedTests: 0,
            failedTests: 0,
            testHistory: []
        };
        console.log('Test statistics reset');
    }
}

// Create global test instance
export const dataReadingTestSuite = new DataReadingTestSuite();

/**
 * Performance monitoring tool
 */
export class PerformanceMonitor {
    metrics: Array<{
        operation: string;
        duration: number;
        timestamp: Date;
    }>;

    constructor() {
        this.metrics = [];
    }

    startTimer(operation: string) {
        const startTime = performance.now();
        return {
            operation,
            startTime,
            end: () => {
                const endTime = performance.now();
                const duration = endTime - startTime;
                this.metrics.push({
                    operation,
                    duration,
                    timestamp: new Date()
                });
                return duration;
            }
        };
    }

    getAverageTime(operation: string): number {
        const operationMetrics = this.metrics.filter(m => m.operation === operation);
        if (operationMetrics.length === 0) return 0;
        
        const total = operationMetrics.reduce((sum, m) => sum + m.duration, 0);
        return total / operationMetrics.length;
    }

    getReport(): Array<{
        operation: string;
        count: number;
        averageTime: number;
        totalTime: number;
    }> {
        const operations = [...new Set(this.metrics.map(m => m.operation))];
        return operations.map(op => ({
            operation: op,
            count: this.metrics.filter(m => m.operation === op).length,
            averageTime: this.getAverageTime(op),
            totalTime: this.metrics.filter(m => m.operation === op).reduce((sum, m) => sum + m.duration, 0)
        }));
    }
}

export const performanceMonitor = new PerformanceMonitor(); 